<script setup lang="ts">
import { ContractGenerationQuestionType, type ContractGenerationTemplateQuestionInDb } from '@/api';
import { describeBounds, knownValues, withinBounds } from '@/utils/contractGeneration/questions';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaDateField from '@/components/design/base/inputs/GaiaDateField.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';

/**
 * The drafting side of a template's questions: one input per question, in the
 * order they were added — the only order there is, since nothing carries an
 * order index.
 *
 * These are **values**, not the comparisons a document answers with — the
 * person says "45,000", and every document whose comparison that satisfies
 * stays in the running. The operator belongs to the document's answer and
 * never appears here, which is what keeps this form simple however the
 * operator set grows.
 *
 * Nothing is required: a question left blank isn't filtered on, so a
 * half-filled form returns more candidates, never fewer.
 */
interface Props {
  questions: ContractGenerationTemplateQuestionInDb[];
  /** question uid → the typed value, in the shape its type calls for. */
  answers: Record<string, string>;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const props = withDefaults(defineProps<Props>(), { disabled: false, size: 'medium' });

const emit = defineEmits<{
  (e: 'update:answers', value: Record<string, string>): void;
}>();

/**
 * Text that isn't a number can't be matched on, and the match request simply
 * leaves it out — so the person would see the candidates *widen* as they typed
 * something wrong, with nothing saying why. Naming it is the whole fix.
 */
function errorFor(question: ContractGenerationTemplateQuestionInDb): string | null {
  const raw = (props.answers[question.uid] ?? '').trim();
  if (raw.length === 0) return null;

  if (
    question.question_type === ContractGenerationQuestionType.NUMBER &&
    !Number.isFinite(Number(raw))
  ) {
    return 'Not a number — this question isn’t narrowing anything yet.';
  }
  // A value outside the question's range comes back as a 422 rather than an
  // empty result, so it is worth catching before the request goes out.
  if (
    question.question_type !== ContractGenerationQuestionType.SELECTION &&
    !withinBounds(question, raw)
  ) {
    return `This question only covers ${describeBounds(question)}.`;
  }
  return null;
}

/**
 * The answers the documents have actually given, which is the whole picker.
 * Nobody authored this list — it is the distinct set of what they serve — so a
 * value outside it could only match nothing.
 */
function optionsFor(question: ContractGenerationTemplateQuestionInDb): {
  label: string;
  value: string;
}[] {
  return knownValues(question).map(value => ({ label: value, value }));
}

function answer(questionUid: string, value: string | null): void {
  const next = { ...props.answers };
  if (value === null || value.trim().length === 0) {
    delete next[questionUid];
  } else {
    next[questionUid] = value;
  }
  emit('update:answers', next);
}
</script>

<template>
  <n-flex vertical :size="12">
    <gaia-form-field
      v-for="question in props.questions"
      :key="question.uid"
      :label="question.label"
      :hint="question.hint ?? describeBounds(question) ?? undefined"
      :error="errorFor(question) ?? undefined"
    >
      <gaia-select
        v-if="question.question_type === ContractGenerationQuestionType.SELECTION"
        :value="props.answers[question.uid] ?? null"
        :options="optionsFor(question)"
        :placeholder="`Any ${question.label.toLowerCase()}`"
        :disabled="props.disabled"
        :size="props.size"
        clearable
        style="width: 100%"
        @update:value="(value: string | null) => answer(question.uid, value)"
      />
      <gaia-input
        v-else-if="question.question_type === ContractGenerationQuestionType.NUMBER"
        :value="props.answers[question.uid] ?? ''"
        placeholder="Any amount"
        :disabled="props.disabled"
        :size="props.size"
        style="width: 100%"
        @update:value="(value: string) => answer(question.uid, value)"
      />
      <gaia-date-field
        v-else
        :value="props.answers[question.uid] ?? null"
        placeholder="Any date"
        :disabled="props.disabled"
        :size="props.size"
        @update:value="(value: string | null) => answer(question.uid, value)"
      />
    </gaia-form-field>
  </n-flex>
</template>
